// Package imports:
import React from 'react';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// Component imports:
import Link from '../../../ui-elements/Link/Link';
import LmdTable from '../../../ui-elements/Table/LmdTable';
import CalculatedColoredNumber from '../../../ui-elements/CalculatedColoredNumber/CalculatedColoredNumber';
import Avatar, { AvatarNames } from '../../../ui-elements/Avatar/Avatar';
import MiniGraph from '../../../ui-elements/MiniGraph/MiniGraph';
import Tooltip from '../../../ui-elements/Tooltip/Tooltip';
// Service imports:
import { displayQuantityShorthand, getLogoSymbol, getTickSizeFractionDigits } from '../../../services/utils';
import { useApiLmdData } from '../../../services/apiHooks';
// Type imports:
import { IApiLmdSnapshot, IHlutabrefChildProps } from '../../../types/HlutabrefTypes';

type IProps = IHlutabrefChildProps;

const ValinHlutabref: React.FC<IProps>= ({
    refreshRateMs,
    accessToken,
    tradables,
    tickSizeTables,
}) => {
    const [ chosenEquities, prevChosenEquities ] = useApiLmdData<IApiLmdSnapshot[]>(
        '/v1/market_data/v1/keldan/exchange/X*/market/*%20SHR%20CCP/symbol/*/delayed_equities',
        accessToken,
        refreshRateMs
    );

    return (
        <LmdTable 
            apiData={chosenEquities}
            columns={[{
                title: 'Auðkenni',
                renderCell: ({symbol, notifications}) => (
                    <div className="avatar-and-text">
                        <a href={`/Markadir/Hlutabref/${symbol}`} aria-label={`Hlutabréf - ${symbol}`}>
                            {(symbol === null)
                                ? null
                                : (
                                    <Avatar
                                        size='sm'
                                        backgroundGrey
                                        name={getLogoSymbol(symbol.toUpperCase()) as AvatarNames}
                                    />
                                )
                            }
                        </a>
                        <Link url={`/Markadir/Hlutabref/${symbol}`} linkSize='13'>
                            {symbol}
                        </Link>
                        {notifications !== undefined && notifications !== null &&
                            <Tooltip
                                text={<FontAwesomeIcon icon={faExclamationCircle} />}
                                tooltip={<>{notifications}</>}
                                noUnderline
                            />
                        }
                    </div>
                ),
                textAlign: 'left',
                simpleSortable: ({symbol}) => symbol
            }, {
                title: 'Verð',
                renderCell: ({last_price, symbol}) => {
                    return (
                        <td style={{ padding: 0 }}>
                            <CalculatedColoredNumber
                                currentValue={last_price}
                                previousValue={prevChosenEquities.data?.find(pd => pd.symbol === symbol)?.last_price ?? null}
                                formatting='decimal'
                                fractionDigits={getTickSizeFractionDigits(symbol, last_price, tradables, tickSizeTables)}
                                hasBackground
                            />
                        </td>
                    )
                },
                overrideTd: true,
                simpleSortable: ({last_price}) => last_price
            }, {
                title: 'Breyting',
                renderCell: ({intraday_per_change, symbol}) => {
                    return (
                        <td style={{ padding: 0 }}>
                            <CalculatedColoredNumber
                                currentValue={intraday_per_change}
                                previousValue={prevChosenEquities.data?.find(pd => pd.symbol === symbol)?.intraday_per_change ?? null}
                                formatting='percent'
                                hasBackground
                                hasColor
                            />
                        </td>
                    )
                },
                overrideTd: true,
                simpleSortable: ({intraday_per_change}) => intraday_per_change
            }, {
                title: 'Sl. 3 Mán',
                renderCell: ({symbol}) => (symbol) && <MiniGraph symbol={symbol} />
            }, {
                title: 'Kaup',
                renderCell: ({bid, symbol}) => {
                    return (
                        <td style={{ padding: 0 }}>
                            <CalculatedColoredNumber
                                currentValue={bid}
                                previousValue={prevChosenEquities.data?.find(pd => pd.symbol === symbol)?.bid ?? null}
                                formatting='decimal'
                                fractionDigits={getTickSizeFractionDigits(symbol, bid, tradables, tickSizeTables)}
                                hasBackground
                            />
                        </td>
                    )
                },
                overrideTd: true,
                simpleSortable: ({bid}) => bid
            }, {
                title: 'Sala',
                renderCell: ({ask, symbol}) => {
                    return (
                        <td style={{ padding: 0}}>
                            <CalculatedColoredNumber
                                currentValue={ask}
                                previousValue={prevChosenEquities.data?.find(pd => pd.symbol === symbol)?.ask ?? null}
                                formatting='decimal'
                                fractionDigits={getTickSizeFractionDigits(symbol, ask, tradables, tickSizeTables)}
                                hasBackground
                            />
                        </td>
                    )
                },
                overrideTd: true,
                simpleSortable: ({ask}) => ask
            }, {
                title: 'Velta',
                renderCell: ({intraday_accumulated_turnover}) => displayQuantityShorthand(intraday_accumulated_turnover),
                simpleSortable: ({intraday_accumulated_turnover}) => intraday_accumulated_turnover
            }]}
        />
    );
}

export default ValinHlutabref;